import React, { useState, useEffect, useMemo } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Collapse, TextField, Grid, Box } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import moment from 'moment';
import Switch from 'react-switch';
import PastableMultiAutocomplete from './PastableMultiAutocomplete';
import { mismaOpcion } from './opciones.js';
import PeriodoAutocomplete from './PeriodoAutocomplete.jsx';
import { rangoDePeriodo } from './periodos.js';
import Security from '../security/Security.js';

/**
 * Panel de filtros reutilizable para grillas.
 *
 * Reemplaza el patrón copiado a mano en los ~22 `*GridFiltros.jsx` del proyecto:
 * un Collapse con inputs, un botón "Buscar" y persistencia en localStorage.
 * Acá eso se declara con una lista de campos en vez de escribirse cada vez.
 *
 * RESPONSABILIDADES
 *  - Renderizar los campos declarados en `fields`.
 *  - Mantener el estado de edición y el estado aplicado (la búsqueda vigente).
 *  - Persistir SIEMPRE el estado aplicado en cada "Buscar" (regla de producto).
 *  - Serializar el estado aplicado a valores primitivos y entregarlo por `onApply`.
 *
 * LO QUE NO HACE (a propósito, queda en la grilla)
 *  - Traducir el query a columnas de DataTables / params del backend: cada grilla
 *    mapea distinto (p.ej. dos campos de fecha que se combinan en un "desde|hasta"
 *    de una sola columna).
 *  - Pedir las opciones de los selects: se pasan ya cargadas por `options`.
 *
 * FORMATO DE SALIDA por tipo de campo:
 *  - text        -> string tal cual
 *  - date        -> string en `dateOutputFormat` (default 'YYYY-MM-DD')
 *  - select      -> el `value` de la opción elegida
 *  - multiselect -> los `value` separados por coma: "1,4,7"
 *  - check       -> '1' si está prendido, '' si no
 *  - periodo     -> DOS claves con el rango derivado (ver abajo)
 *  - pastableMultiselect -> DOS claves (ver abajo)
 *
 * DOS PROPS OPCIONALES DE CAMPO, para los tipos `text` y `date` (VEC-3690)
 *  - `validate(valor, estadoCompleto)` -> texto de error o vacío. Se muestra debajo del
 *    campo, en rojo. Es un CARTEL, no bloquea el "Buscar". Recibe el estado entero porque
 *    lo habitual es validar entre campos (un "hasta" menor que su "desde").
 *  - `inputProps` -> se pasa derecho al <input> de MUI. Sirve para conservar lo que el
 *    campo ya traía de su panel de origen: `{ type: 'number', min: 0 }`, `maxLength`, etc.
 * Un campo que no las declara se comporta EXACTAMENTE como antes.
 *
 * EL OTRO CASO ESPECIAL: `periodo`
 * Monta PeriodoAutocomplete (atajos "Hoy" / "Últimos 30 días" / "Este mes" ... más un
 * rango personalizado con dos DatePicker). En el estado vive la OPCIÓN elegida, pero al
 * serializar se emite el RANGO en dos claves y no la opción:
 *   - `startName` -> desde, en 'YYYY-MM-DD'  (default `<name>Inicio`)
 *   - `endName`   -> hasta, en 'YYYY-MM-DD'  (default `<name>Final`)
 * La clave `name` no viaja: es UI. Los períodos relativos se recalculan en cada
 * serialización, así un "Hoy" guardado ayer sigue significando hoy.
 *
 * EL CASO ESPECIAL: `pastableMultiselect`
 * Monta PastableMultiAutocomplete (selector múltiple que además acepta pegar una lista
 * separada por coma o punto y coma). Es el único tipo que produce DOS claves de query,
 * porque su valor tiene dos partes que el backend recibe por separado:
 *   - `name`     -> los chips resueltos, "12,15" o tipados "Moviles:12,Llantas:5"
 *   - `textName` -> el texto que quedó SIN resolver, para búsqueda parcial
 * Declararlo:
 *   { name: 'entidades', textName: 'entidad', type: 'pastableMultiselect',
 *     onSearch: (busqueda) => Promise<[{value,label,tipo?}]>,
 *     onResolve: (valores) => Promise<{encontrados,noEncontrados}> }
 * Si no se declara `textName` se usa `<name>Texto`. Las opciones NO salen de `options`:
 * este tipo las pide al servidor mientras se tipea, por eso trae sus propios callbacks.
 */

const TIPOS_MULTIPLES = ['multiselect'];

const TIPO_PASTABLE = 'pastableMultiselect';

const TIPO_CHECK = 'check';

const TIPO_PERIODO = 'periodo';

// Colores del switch, los mismos que usan hoy los ~20 paneles de filtros escritos a
// mano (personas, moviles, rutas, motivos...). Se centralizan acá para no seguir
// repitiéndolos por archivo.
const SWITCH_OFF = '#FF4961';
const SWITCH_ON = '#28D094';

const SIZE_DEFAULT = { xs: 12, sm: 6, md: 3, lg: 2 };

const FORMATO_INPUT_DATE = 'YYYY-MM-DD';

/**
 * Mensaje de error de un campo, si declara `validate`.
 *
 * `validate(valor, estadoCompleto)` devuelve el texto a mostrar o algo vacío si está bien.
 * Recibe el estado entero porque las validaciones típicas son entre campos (un "hasta" que
 * no puede ser menor que su "desde"). Es SOLO UN CARTEL: no deshabilita el botón Buscar —
 * para eso está `requireAlgunFiltro`, que es una decisión distinta.
 */
export const mensajeDeError = (field, filtersState) => {
	if (typeof field.validate !== 'function') return '';
	const mensaje = field.validate(filtersState[field.name], filtersState);
	return mensaje ? mensaje : '';
};

/** Campos visibles: `visible` en false permite gatear por config flag o permiso. */
const camposVisibles = (fields) => (fields || []).filter((f) => f && f.visible !== false);

/** Lleva a array lo que venga de una persistencia previa a que el campo fuera múltiple. */
const aArray = (valor) => {
	if (!valor) return [];
	return Array.isArray(valor) ? valor : [valor];
};

/** Clave de query donde viaja el texto libre de un campo `pastableMultiselect`. */
const claveTexto = (field) => field.textName || field.name + 'Texto';

/** Claves de query donde viaja el rango derivado de un campo `periodo`. */
const claveDesde = (field) => field.startName || field.name + 'Inicio';
const claveHasta = (field) => field.endName || field.name + 'Final';

/**
 * Normaliza el valor compuesto de un `pastableMultiselect`. Tolera las formas que
 * puede tener una persistencia previa: array pelado (era multiselect), string
 * (era text) o el objeto ya armado.
 */
const aValorPastable = (valor) => {
	if (!valor) return { seleccionados: [], texto: '' };
	if (Array.isArray(valor)) return { seleccionados: valor, texto: '' };
	if (typeof valor === 'string') return { seleccionados: [], texto: valor };
	return { seleccionados: aArray(valor.seleccionados), texto: valor.texto || '' };
};

/** Valor "vacío" de un campo según su tipo. */
const valorVacio = (field) => {
	if (field.type === TIPO_CHECK) return false;
	if (field.type === TIPO_PASTABLE) return { seleccionados: [], texto: '' };
	if (TIPOS_MULTIPLES.indexOf(field.type) !== -1) return [];
	return field.type === 'select' || field.type === TIPO_PERIODO ? null : '';
};

/**
 * Estado inicial derivado de la declaración de campos. Se exporta porque las
 * grillas lo necesitan para construir su query por defecto sin repetir la lista.
 */
export const buildEmptyFilterState = (fields) => {
	const estado = {};
	(fields || []).forEach((field) => {
		if (field && field.name) estado[field.name] = valorVacio(field);
	});
	return estado;
};

/**
 * Normaliza una fecha al formato que necesita `<input type="date">`. Acepta que el
 * valor persistido esté en el formato de salida (p.ej. DD/MM/YYYY) y lo reconoce
 * por la barra, para no reformatear de más.
 */
const aFechaInput = (valor, formatoSalida) => {
	if (!valor) return '';
	const texto = String(valor);
	const formato = texto.indexOf('/') !== -1 ? (formatoSalida || FORMATO_INPUT_DATE) : FORMATO_INPUT_DATE;
	const m = moment(texto, formato, true);
	return m.isValid() ? m.format(FORMATO_INPUT_DATE) : '';
};

/** Un valor persistido puede volver como boolean, como 'true'/'false' o como 1/0. */
const aBooleano = (valor) => valor === true || valor === 'true' || valor === 1 || valor === '1';

/** Serializa un campo del estado a un valor primitivo listo para el backend. */
const serializarCampo = (field, valor) => {
	// '1' / '' y NO true / 'false': mantiene la convención de que vacío = sin filtro,
	// y sobre todo evita el clásico string 'false', que es truthy en JS y pasa el
	// !empty() de PHP. Si una grilla necesita otros valores, los mapea en su propio
	// handler (traducir el query es responsabilidad de la grilla).
	if (field.type === TIPO_CHECK) {
		return aBooleano(valor) ? '1' : '';
	}
	if (TIPOS_MULTIPLES.indexOf(field.type) !== -1) {
		if (!valor) return '';
		const lista = Array.isArray(valor) ? valor : [valor];
		return lista.map((e) => (e && e.value !== undefined ? e.value : e)).join(',');
	}
	if (field.type === 'select') {
		if (!valor) return '';
		return valor.value !== undefined ? valor.value : '';
	}
	if (field.type === 'date') {
		if (!valor) return '';
		const texto = String(valor);
		const formatoSalida = field.dateOutputFormat || FORMATO_INPUT_DATE;
		// Ya viene en el formato de salida (persistencia previa): se pasa tal cual.
		if (formatoSalida.indexOf('/') !== -1 && texto.indexOf('/') !== -1) return texto;
		const m = moment(texto, FORMATO_INPUT_DATE, true);
		return m.isValid() ? m.format(formatoSalida) : '';
	}
	return valor === undefined || valor === null ? '' : valor;
};

/**
 * Serializa un estado completo. Exportada para que la grilla pueda calcular su
 * query por defecto con las MISMAS reglas que usa el panel al aplicar.
 */
export const serializeFilterState = (fields, state) => {
	const query = {};
	(fields || []).forEach((field) => {
		if (!field || !field.name) return;
		const valor = state ? state[field.name] : undefined;

		// El período aporta DOS claves con el rango derivado, y su propio `name` NO viaja:
		// la opción elegida es UI, el backend recibe fechas.
		if (field.type === TIPO_PERIODO) {
			const rango = rangoDePeriodo(valor);
			query[claveDesde(field)] = rango.inicio;
			query[claveHasta(field)] = rango.fin;
			return;
		}

		// Aporta DOS claves también: los chips y el texto sin resolver.
		if (field.type === TIPO_PASTABLE) {
			const compuesto = aValorPastable(valor);
			query[field.name] = compuesto.seleccionados
				.map((e) => (e && e.tipo ? e.tipo + ':' + e.value : (e && e.value !== undefined ? e.value : e)))
				.join(',');
			query[claveTexto(field)] = compuesto.texto || '';
			return;
		}

		query[field.name] = serializarCampo(field, valor);
	});
	return query;
};

/** Normaliza un estado hidratado (de localStorage) al shape que esperan los inputs. */
const normalizarEstado = (fields, estado) => {
	const normalizado = Object.assign({}, estado);
	(fields || []).forEach((field) => {
		if (!field || !field.name) return;
		const valor = normalizado[field.name];
		if (TIPOS_MULTIPLES.indexOf(field.type) !== -1) {
			normalizado[field.name] = aArray(valor);
		} else if (field.type === 'date') {
			normalizado[field.name] = aFechaInput(valor, field.dateOutputFormat);
		}
	});
	return normalizado;
};

const GridFilterPanel = (props) => {
	const intl = useIntl();

	const fields = camposVisibles(props.fields);
	// La clave va acotada al usuario (VEC-3769): en un dispositivo compartido, con una clave
	// fija el que entra hereda los filtros del anterior. Acá y no en cada grilla porque es el
	// único lugar por el que pasan las 7 que usan el panel.
	// `migrarClaveSinUsuario` es TEMPORAL (ver Security.js): adopta la entrada sin usuario que
	// dejó la versión anterior. Va acá y no en un useEffect porque el estado inicial se arma en
	// el mismo render: desde un efecto llegaría tarde y el primer render saldría sin filtros.
	const storageKey = useMemo(
		() => (props.storageKey ? Security.migrarClaveSinUsuario(props.storageKey) : null),
		[props.storageKey]
	);
	const options = props.options || {};

	const estadoBase = () => Object.assign({}, buildEmptyFilterState(fields), props.defaults || {});

	const leerPersistido = () => {
		if (!storageKey) return null;
		try {
			const guardado = localStorage.getItem(storageKey);
			if (guardado) return JSON.parse(guardado);
		} catch (e) {}
		return null;
	};

	const estadoInicial = () => {
		const guardado = leerPersistido();
		if (guardado) {
			// `activo` era el flag del switch "Persistir Filtros", que ya no existe
			// (la persistencia es siempre). Se descarta si viene de una entrada vieja.
			const filtros = Object.assign({}, guardado);
			delete filtros.activo;
			return normalizarEstado(fields, Object.assign({}, estadoBase(), filtros));
		}
		return normalizarEstado(fields, estadoBase());
	};

	const [filtersState, setFiltersState] = useState(estadoInicial);
	const [appliedFiltersState, setAppliedFiltersState] = useState(estadoInicial);

	// Única fuente de verdad para localStorage. Se persiste SIEMPRE, en cada
	// "Buscar": appliedFiltersState solo cambia al aplicar, así que escribir acá
	// equivale a persistir por búsqueda, sin closures stale en los handlers.
	useEffect(() => {
		if (storageKey) localStorage.setItem(storageKey, JSON.stringify(appliedFiltersState));
	}, [storageKey, appliedFiltersState]);

	// Con `applyOnMount` el panel entrega su estado inicial —que puede venir de
	// localStorage— en cuanto monta. Lo necesitan las grillas que traen los datos ellas
	// mismas a partir del query (las de MUI): sin esto, la primera carga saldría con el
	// query por defecto y los filtros persistidos se verían en los controles pero no
	// estarían aplicados. Las grillas de DataTables no lo usan: su `stateSave` ya restaura
	// la búsqueda por columna.
	useEffect(() => {
		if (props.applyOnMount && props.onApply) {
			props.onApply(serializeFilterState(fields, appliedFiltersState));
		}
	}, []);

	// Reset pedido desde afuera (botón "Restaurar filtros" o la "x" de la franja
	// de resultados). No dispara onApply: la grilla ya aplica su query por defecto.
	useEffect(() => {
		if (props.resetSignal) {
			const reset = normalizarEstado(fields, estadoBase());
			setFiltersState(reset);
			setAppliedFiltersState(reset);
			if (props.onResetSignalHandled) props.onResetSignalHandled();
		}
	}, [props.resetSignal]);

	// Estado impuesto desde la grilla, para las acciones que eligen un filtro por el
	// usuario (las tarjetas de KPI de tickets: "ver los EN TALLER"). NO dispara onApply,
	// igual que el reset: quien lo pide ya sabe qué query quiere: el panel solo sincroniza
	// sus controles con esa decisión.
	//
	// Por defecto se impone sobre `defaults`, o sea que lo demás se limpia. Con
	// `overrideMerge` se impone sobre lo que el usuario ya tenía cargado, que es lo que
	// hace el clasificador de estados de Móviles: acota lo que ya estaba filtrado.
	useEffect(() => {
		if (props.overrideSignal) {
			const base = props.overrideMerge ? filtersState : estadoBase();
			const impuesto = normalizarEstado(fields, Object.assign({}, base, props.overrideState || {}));
			setFiltersState(impuesto);
			// `overrideDraftOnly`: la corrección es del BORRADOR y no de lo aplicado. Quien
			// impone un filtro Y YA CONSULTÓ con él (las tarjetas de tickets, el clasificador de
			// móviles) necesita que `appliedFiltersState` acompañe; quien sólo está arreglando lo
			// que el usuario tiene cargado, no: `appliedFiltersState` es lo que se persiste, así
			// que escribirlo acá guardaría el borrador entero —esa edición y cualquier otra
			// pendiente— sin que nadie haya apretado Buscar (review del PR 2387, VEC-3696).
			if (!props.overrideDraftOnly) setAppliedFiltersState(impuesto);
			if (props.onOverrideSignalHandled) props.onOverrideSignalHandled();
		}
	}, [props.overrideSignal]);

	const aplicar = (estado) => {
		const actual = estado !== undefined ? estado : filtersState;
		setAppliedFiltersState(actual);
		if (props.onApply) props.onApply(serializeFilterState(fields, actual));
	};

	/**
	 * `onFieldChange(name, valor, estado)` — OPCIONAL, agregada en VEC-3696.
	 *
	 * Avisa de una edición APENAS pasa, sin esperar el "Buscar". La necesita el consumidor que
	 * tiene un desplegable DEPENDIENTE de otro: en Móviles Inmovilizados las opciones de
	 * `modelo` salen de `/modelos/marca/{id}/select`, y hasta ahora el panel sólo hablaba al
	 * aplicar (`onApply`), así que el wrapper no tenía cómo enterarse de la marca elegida.
	 *
	 * Es puramente aditiva: sin la prop, nada cambia para los otros consumidores.
	 *
	 * OJO AL USARLA: se dispara en CADA tecla de un campo de texto. Si adentro se le pega al
	 * backend, filtrar por `name` (o debouncear); para un `select` como marca no hace falta.
	 * El tercer argumento es el estado YA actualizado, porque el del render todavía no lo está.
	 */
	const avisarCambio = (name, valor, estado) => {
		if (props.onFieldChange) props.onFieldChange(name, valor, estado);
	};

	const handleChange = (name, valor) => {
		// El aviso va AFUERA del updater: el updater tiene que ser puro (React puede
		// invocarlo más de una vez) y la actualización del estado queda exactamente como
		// estaba antes de esta prop.
		setFiltersState((prev) => Object.assign({}, prev, { [name]: valor }));
		avisarCambio(name, valor, Object.assign({}, filtersState, { [name]: valor }));
	};

	// Un check aplica la búsqueda AL INSTANTE, sin esperar el botón "Buscar". Es como se
	// comporta hoy en los paneles escritos a mano (PersonasGridFiltros::handleSwitch
	// llama a triggerFilter) y es lo esperable en un toggle binario. Se aplica el estado
	// ya actualizado y no el del state, que todavía no se re-renderizó.
	const handleChangeCheck = (name, prendido) => {
		const actualizado = Object.assign({}, filtersState, { [name]: prendido });
		setFiltersState(actualizado);
		avisarCambio(name, prendido, actualizado);
		aplicar(actualizado);
	};

	// Merge parcial sobre el valor compuesto de un `pastableMultiselect`. Se calcula
	// desde `prev` y NO desde una copia del render: al resolver una lista pegada el
	// componente llama onSeleccionChange y onTextoChange en el MISMO tick, y con un
	// valor capturado el segundo pisaría al primero (perdería los chips recién
	// resueltos, o el reporte de los no encontrados).
	const handleChangePastable = (name, cambio) => {
		setFiltersState((prev) => {
			const actual = aValorPastable(prev[name]);
			return Object.assign({}, prev, { [name]: Object.assign({}, actual, cambio) });
		});
	};

	// Enter en un campo de texto aplica la búsqueda con ese valor ya incorporado
	// (no se puede confiar en el state, que todavía no se re-renderizó).
	const handleOnEnter = (e, name) => {
		if (e.keyCode === 13) {
			e.preventDefault();
			// El Enter equivale al botón Buscar, así que respeta el mismo bloqueo.
			if (sinFiltros) return;
			aplicar(Object.assign({}, filtersState, { [name]: e.target.value }));
		}
	};

	const etiqueta = (field) => {
		if (field.messageId) {
			return intl.formatMessage({ id: field.messageId, defaultMessage: field.label || field.name });
		}
		return field.label || field.name;
	};

	// Los checks NO van en la grilla de campos: viven en la fila del botón "Buscar",
	// que es donde están hoy en todas las grillas. Un switch en una celda de 2 columnas
	// con el label arriba queda roto.
	const renderCheck = (field) => (
		<Grid component={Box} item key={field.name} xs="auto" style={{ paddingRight: 20 }}>
			<div className="d-flex align-items-center" style={{ gap: 8 }}>
				<span>{etiqueta(field)}</span>
				<Switch
					onChange={(prendido) => handleChangeCheck(field.name, prendido)}
					checked={aBooleano(filtersState[field.name])}
					disabled={props.loading}
					offColor={SWITCH_OFF}
					onColor={SWITCH_ON}
				/>
			</div>
		</Grid>
	);

	const renderCampo = (field) => {
		const texto = etiqueta(field);
		const valor = filtersState[field.name];
		const error = mensajeDeError(field, filtersState);

		if (field.type === 'select' || field.type === 'multiselect') {
			const esMultiple = field.type === 'multiselect';
			return (
				<Autocomplete
					size="small"
					name={field.name}
					multiple={esMultiple}
					disabled={props.loading}
					options={options[field.name] || []}
					value={esMultiple ? (valor ? valor : []) : (valor !== undefined ? valor : null)}
					defaultValue={esMultiple ? [] : null}
					getOptionLabel={(option) => (option ? option.label : '')}
					// Sin esto MUI compara las opciones por identidad de objeto y no reconoce lo que
					// ya está elegido: tanto el valor por defecto (que se construye aparte) como el
					// que vuelve de localStorage (que pasó por JSON.parse) son otra instancia, así
					// que al clickearlos otra vez se agregaba un chip DUPLICADO en lugar de quitarlos.
					getOptionSelected={mismaOpcion}
					onChange={(_, seleccion) => handleChange(field.name, seleccion)}
					renderInput={(params) => (
						<TextField {...params} variant="standard" label={texto} placeholder={texto} />
					)}
				/>
			);
		}

		if (field.type === TIPO_PERIODO) {
			return (
				<PeriodoAutocomplete
					name={field.name}
					label={texto}
					placeholder={field.placeholder}
					disabled={props.loading}
					options={options[field.name] || []}
					value={valor}
					onChange={(option) => handleChange(field.name, option)}
				/>
			);
		}

		if (field.type === TIPO_PASTABLE) {
			const compuesto = aValorPastable(valor);
			return (
				<PastableMultiAutocomplete
					label={texto}
					placeholder={field.placeholder || texto}
					disabled={props.loading}
					seleccionados={compuesto.seleccionados}
					texto={compuesto.texto}
					onSeleccionChange={(nuevos) => handleChangePastable(field.name, { seleccionados: nuevos })}
					onTextoChange={(nuevo) => handleChangePastable(field.name, { texto: nuevo })}
					onSearch={field.onSearch}
					onResolve={field.onResolve}
				/>
			);
		}

		if (field.type === 'date') {
			return (
				<TextField
					type="date"
					label={texto}
					name={field.name}
					value={valor || ''}
					onChange={(e) => handleChange(field.name, e.target.value)}
					className="col-12"
					InputLabelProps={{ shrink: true }}
					disabled={props.loading}
					error={error !== ''}
					helperText={error}
				/>
			);
		}

		return (
			<TextField
				label={texto}
				name={field.name}
				value={valor || ''}
				onChange={(e) => handleChange(field.name, e.target.value)}
				onKeyDown={(e) => handleOnEnter(e, field.name)}
				className="col-12"
				disabled={props.loading}
				error={error !== ''}
				helperText={error}
				// `inputProps` pasa derecho al <input>: sirve para conservar cosas que el campo
				// ya tenía en su panel de origen (type="number", min, maxLength...).
				inputProps={field.inputProps}
			/>
		);
	};

	const campos = fields.filter((f) => f.type !== TIPO_CHECK);
	const checks = fields.filter((f) => f.type === TIPO_CHECK);

	// Con `requireAlgunFiltro` el botón Buscar se deshabilita mientras no haya NINGÚN
	// campo cargado, para no lanzar la consulta más cara posible por accidente. Se compara
	// contra el vacío de cada tipo y no por truthy, porque los valores compuestos
	// (`pastableMultiselect`, `periodo`) son objetos y siempre serían truthy.
	const sinFiltros =
		props.requireAlgunFiltro === true &&
		fields.every(
			(field) => JSON.stringify(filtersState[field.name]) === JSON.stringify(valorVacio(field))
		);

	return (
		<Collapse in={props.open}>
			<Grid container item xs={12} spacing={2} style={{ marginBottom: 16, marginTop: 4 }}>
				{campos.map((field) => {
					const size = Object.assign({}, SIZE_DEFAULT, field.size || {});
					return (
						<Grid component={Box} item key={field.name} xs={size.xs} sm={size.sm} md={size.md} lg={size.lg}>
							{renderCampo(field)}
						</Grid>
					);
				})}
				<Grid container item xs={12} spacing={2} alignItems="center">
					{checks.map(renderCheck)}
					<Grid component={Box} item style={{ flex: 1 }} />
					<Grid component={Box} item xs="auto">
						<button
							disabled={props.loading || sinFiltros}
							className="btn btn-primary"
							onClick={() => aplicar()}
						>
							<i className="fa fa-search fa-xs"></i> <FormattedMessage id="Buscar" defaultMessage="Buscar" />
						</button>
					</Grid>
				</Grid>
			</Grid>
		</Collapse>
	);
};

export default GridFilterPanel;
